'use client';

import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { clsx } from 'clsx';

/* Resources
 *
 * Shadcn: https://ui.shadcn.com/docs/components/tooltip
 * Radix: https://www.radix-ui.com/primitives/docs/components/tooltip#api-reference
 *
 * */

export function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

export function TooltipSkeleton({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  );
}

export function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

export type TooltipContentColorMode = 'neutral' | 'primary' | 'secondary';

export function TooltipContent({
  className,
  sideOffset = 0,
  children,
  colorMode = 'neutral',
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> & {
  colorMode?: TooltipContentColorMode;
}) {
  function getContentColorClass(colorMode: 'neutral' | 'primary' | 'secondary') {
    switch (colorMode) {
      case 'primary':
        return 'bg-primary text-primary-content';
      case 'secondary':
        return 'bg-secondary text-secondary-content';
      default:
        return 'bg-neutral text-neutral-content';
    }
  }

  function getArrowColorClass(colorMode: 'neutral' | 'primary' | 'secondary') {
    switch (colorMode) {
      case 'primary':
        return 'bg-primary fill-primary';
      case 'secondary':
        return 'bg-secondary fill-secondary';
      default:
        return 'bg-neutral fill-neutral';
    }
  }

  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={clsx(
          'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-(--radix-tooltip-content-transform-origin) rounded-selector z-50 w-fit max-w-40 text-balance px-3 py-1.5 text-xs sm:max-w-80',
          getContentColorClass(colorMode),
          className,
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow
          className={clsx(
            'z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]',
            getArrowColorClass(colorMode),
          )}
        />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}
